import type { FlightCardData } from '@massilia/queries'

import { cn, Separator } from '@massilia/ui'
import { Backpack, ChevronDown, ChevronUp, Luggage } from 'lucide-react'
import { toast } from 'sonner'

import { useDebug } from './debug-context'
import { FlightCardSegment } from './flight-card-segment'

interface FlightInfoSectionProps {
  flight: FlightCardData
  hasAlternativeOptions?: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
  totalOptionsCount?: number
}

export function FlightInfoSection({
  flight,
  hasAlternativeOptions = false,
  isExpanded = false,
  onToggleExpand,
  totalOptionsCount = 0,
}: FlightInfoSectionProps) {
  const showDebug = useDebug()
  // Check if this is a multi-destination flight
  const isMultiDestination = flight.segments && flight.segments.length > 0
  const hasReturn = !isMultiDestination && flight.return

  // Get luggage info from flight data (calculated by backend)
  const cabinBags = flight.minCabinBags
  const cabinBagsWeightKg = flight.minCabinBagsWeightKg
  const checkedBags = flight.minCheckedBags
  const checkedBagsWeightKg = flight.minCheckedBagsWeightKg ?? 23
  const totalCheckedWeight = checkedBags * checkedBagsWeightKg
  const checkedBagBgClass =
    totalCheckedWeight >= 46
      ? 'bg-green-600'
      : totalCheckedWeight >= 30
        ? 'bg-green-500'
        : 'bg-green-400'

  return (
    <>
      {/* Multi-destination segments */}
      {isMultiDestination && flight.segments ? (
        <div className="space-y-2">
          {flight.segments.map((segment, index) => (
            <div
              className={index > 0 ? 'border-t border-gray-100 pt-4' : ''}
              key={`segment-${index}`}
            >
              <FlightCardSegment segment={segment} segmentType={index + 1} />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Outbound flight segment */}
          <FlightCardSegment segment={flight.outbound} segmentType="outbound" />
          {/* Return flight segment if round trip */}
          {hasReturn && flight.return && (
            <div className="border-t border-gray-100 pt-4">
              <FlightCardSegment segment={flight.return} segmentType="return" />
            </div>
          )}
        </div>
      )}

      <Separator className="my-2" />
      {/* Bottom section: Duration, payment info and luggage */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Left: Luggage info */}
        <div className="flex flex-col items-start gap-3 text-xs text-gray-600">
          {/* Cabin baggage */}
          <div className="flex items-center gap-1.5">
            <Backpack className="size-4 text-gray-400" />
            {cabinBags > 0 ? (
              <span className="rounded bg-green-500 px-1.5 py-0.5 text-xs font-bold text-white">
                {cabinBagsWeightKg != null
                  ? `Cabine · ${cabinBags} × ${cabinBagsWeightKg} kg`
                  : `Cabine · ${cabinBags} bag`}
              </span>
            ) : (
              <span>Pas de bagage cabine</span>
            )}
          </div>
          {/* Checked baggage */}
          <div className="flex items-center gap-1.5">
            <Luggage className="size-4 text-gray-400" />
            {checkedBags > 0 ? (
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-xs font-bold text-white',
                  checkedBagBgClass
                )}
              >
                {checkedBags > 1
                  ? `Soute · ${checkedBags} × ${checkedBagsWeightKg} kg (${totalCheckedWeight} kg)`
                  : `Soute · ${totalCheckedWeight} kg`}
              </span>
            ) : (
              <span>Pas de bagage en soute</span>
            )}
          </div>
        </div>

        {flight.isInstallmentEligibleAtSearch ? (
          <div className="flex items-center gap-2 text-gray-700">
            <img alt="Massilia Voyages" className="h-5 w-auto" src="/logos/massilia-logo.png" />
            <div className="leading-tight">
              <p className="text-xs font-medium">Paiement en plusieurs fois</p>
              <p className="text-xs">
                sans frais <span className="font-bold">Massilia Voyages</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-700">
            <img alt="Oney" className="h-5 w-auto" src="/logos/floa-logo.png" />
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium">Paiement en</span>
              <span className="rounded-full bg-sky-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                3X
              </span>
              <span>
                avec <span className="font-bold uppercase">floa</span>
              </span>
            </div>
          </div>
        )}

        {/* Right: Journey options toggle or payment link */}
        {hasAlternativeOptions && onToggleExpand ? (
          <button
            className="hover:to-sky-150 flex items-center gap-1.5 rounded-full border border-sky-700 bg-linear-to-r from-sky-50 to-sky-100 px-3 py-1 text-xs text-sky-700 hover:cursor-pointer hover:from-sky-100"
            onClick={onToggleExpand}
            type="button"
          >
            <span className="font-medium">
              Autres horaires disponibles <b>{totalOptionsCount}</b>
            </span>
            {isExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
          </button>
        ) : (
          <span className="text-xs text-gray-400">Pas d&apos;alternative horaire</span>
        )}
      </div>

      {/* Debug: Transaction ID */}
      {showDebug && (
        <div className="mt-2 rounded border border-yellow-200 bg-yellow-50 p-2 font-mono text-xs text-yellow-800">
          <span className="font-semibold">Transaction ID:</span>{' '}
          <button
            className="cursor-pointer select-all hover:underline"
            onClick={() => {
              void navigator.clipboard.writeText(flight.transactionId).then(() => {
                toast.info('Transaction ID copiée dans le presse-papier')
              })
            }}
            title="Cliquez pour copier dans le presse-papier"
          >
            <span className="break-all">{flight.transactionId}</span>
          </button>
        </div>
      )}

      {/* Debug: Installment rule */}
      {showDebug && flight.installmentMatchingRuleId && (
        <div className="mt-2 rounded border border-green-200 bg-green-50 p-2 font-mono text-xs text-green-800">
          <span className="font-bold">Règle de paiement en plusieurs fois:</span>{' '}
          <button
            className="cursor-pointer hover:underline"
            onClick={() => {
              void navigator.clipboard.writeText(flight.installmentMatchingRuleId!).then(() => {
                toast.info('ID copié')
              })
            }}
          >
            {flight.installmentMatchingRuleId}
          </button>
        </div>
      )}

      {/* Debug: ID de l'offre */}
      {showDebug && flight.debugId && (
        <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-600">
          <span className="font-bold">ID :</span> {flight.debugId}
        </div>
      )}

      {/* Debug: R9 - Bagage forcé */}
      {showDebug && flight.originalCheckedBags !== undefined && (
        <div className="mt-2 rounded border border-orange-200 bg-orange-50 p-2 font-mono text-xs text-orange-800">
          <span className="font-bold">R9 :</span> Bagage initialement à {flight.originalCheckedBags}
          PC, forcé à {flight.minCheckedBags}PC
        </div>
      )}

      {/* Debug: R14 - Poids bagage forcé */}
      {showDebug && flight.originalCheckedBagsWeightKg !== undefined && (
        <div className="mt-2 rounded border border-orange-200 bg-orange-50 p-2 font-mono text-xs text-orange-800">
          <span className="font-bold">R14 :</span> Poids bagage initialement à{' '}
          {flight.originalCheckedBagsWeightKg}kg, forcé à {flight.minCheckedBagsWeightKg ?? 32}kg
        </div>
      )}

      {/* Debug: Règles appliquées */}
      {showDebug && flight.appliedRules && flight.appliedRules.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {flight.appliedRules.map((rule) => (
            <span
              className="rounded border border-red-200 bg-red-50 px-1.5 py-0.5 font-mono text-xs font-bold text-red-700"
              key={rule}
            >
              {rule}
            </span>
          ))}
          {flight.collidedWithDebugId && (
            <button
              className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-mono text-xs font-bold text-blue-700 hover:bg-blue-100"
              onClick={() => {
                const el = document.getElementById(`offer-${flight.collidedWithDebugId}`)
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                el?.classList.add('ring-2', 'ring-blue-500')
                setTimeout(() => el?.classList.remove('ring-2', 'ring-blue-500'), 2000)
              }}
            >
              → Voir {flight.collidedWithDebugId}
            </button>
          )}
        </div>
      )}
    </>
  )
}
